import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Drawer,
  Button,
  Space,
  Typography,
  Tooltip,
  message,
  Tag,
  Tabs,
  Alert,
  Spin,
  Input,
  Select,
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  SwapOutlined,
  CompressOutlined,
  FileAddOutlined,
  HighlightOutlined,
  SearchOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import MDEditor from '@uiw/react-md-editor';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { updateAgent, draftInstruction, getAgentInstructions } from '@/api/agents';
import type {
  AgentDefinition,
  AgentOverviewBundle,
} from '@/api/types';
import type {
  InstructionDraftMode,
  InstructionRedundancyHit,
} from '@/api/agents';
import { InstructionHistoryDrawer } from './InstructionHistoryDrawer';
import { useHoldUnsavedWork } from '@/components/practiceSwitch';

const { Text } = Typography;
const { TextArea } = Input;

// ─────────────────────────────────────────────────────────────────────────────
// InstructionEditorDrawer — smart, AI-assisted editor for the agent's
// ai.AiAgent.Instructions column.
//
// Three-pane layout:
//   1. Editor       — @uiw/react-md-editor (Markdown w/ syntax highlighting,
//                     live preview toggle, full-screen). Section folding via
//                     ALL-CAPS heading detection.
//   2. Preview pane — rendered Markdown of the EFFECTIVE prompt (primary
//                     instruction concatenated with linked-capability
//                     segments) so the user sees the runtime view.
//   3. Smart sidebar — AI co-pilot with four flavors (tighten, add section,
//                     rewrite selection, find redundancy), token budget
//                     meter, lint warnings, linked capability list.
//
// Saves go through PUT /api/Agent/Update (existing endpoint) — only
// the `instructions` column changes. "Export .md" downloads the current
// body for git history per the cloud-native convention.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  agent: AgentDefinition | null;
  /** Capability segments concatenated with the primary instruction at runtime. Optional but unlocks redundancy + effective-prompt preview. */
  overview?: AgentOverviewBundle | null;
  onClose: () => void;
  onSaved?: (next: AgentDefinition) => void | Promise<void>;
}

interface DraftSnapshot {
  mode: InstructionDraftMode;
  proposedBody: string | null;
  rationale: string;
  redundancy: InstructionRedundancyHit[];
}

// Approximate token count: chars / 4. Cheap, close enough for a meter.
const tokensFor = (s: string) => Math.ceil((s?.length ?? 0) / 4);

// 200k context limit is the typical Opus/Sonnet ceiling. Color thresholds
// are conservative — alert long before the runtime would actually reject.
const TOKEN_BUDGET_LIMIT = 100_000;

export const InstructionEditorDrawer = observer(function InstructionEditorDrawer({
  open,
  agent,
  overview,
  onClose,
  onSaved,
}: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  // Working buffer — independent from the saved DB row so the user can
  // experiment, draft with AI, and only commit on Save.
  const [body, setBody] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [draftSnapshot, setDraftSnapshot] = useState<DraftSnapshot | null>(null);
  const [previousBody, setPreviousBody] = useState<string | null>(null);
  const [draftPrompt, setDraftPrompt] = useState('');
  const [previewMode, setPreviewMode] = useState<'primary' | 'effective'>('primary');

  // Track the user's caret/selection inside the editor so the "Rewrite
  // selection" co-pilot has something to operate on. MDEditor's textareaProps
  // doesn't accept a typed ref, so we anchor a wrapper div and query the
  // textarea inside it on mouseup / keyup.
  const editorWrapRef = useRef<HTMLDivElement | null>(null);
  const [selectionText, setSelectionText] = useState<string>('');

  // Reset the buffer whenever the drawer opens or the agent changes — this
  // is the boundary between "the saved truth" and "what the user is editing".
  useEffect(() => {
    if (!open) return;

    // Seed from whatever the caller already has, so the editor paints immediately
    // rather than flashing empty while the network call is out.
    setBody(agent?.instructions ?? '');
    setDraftSnapshot(null);
    setPreviousBody(null);
    setDraftError(null);
    setDraftPrompt('');
    setSelectionText('');

    // Then read the body THROUGH to the database and correct the buffer.
    //
    // Two reasons this is not redundant. The agent may have been loaded a while ago
    // and edited elsewhere since — this is the one screen where saving a stale body
    // silently reverts someone else's work. And api/Agent/Search no longer returns
    // `instructions` at all, so any caller that hands this drawer a list-sourced agent
    // would otherwise open an empty editor over an agent that has a prompt, and a save
    // would look like a deliberate wipe.
    //
    // Only applied while the buffer is still untouched, so it can never overwrite
    // something the user has already started typing.
    const agentId = agent?.id;
    if (agentId == null) return;

    let cancelled = false;
    const seeded = agent?.instructions ?? '';

    void getAgentInstructions(agentId)
      .then((serverBody) => {
        if (cancelled) return;
        setBody((current) => (current === seeded ? serverBody : current));
      })
      .catch(() => {
        // Non-fatal: keep whatever the caller gave us. The save path is still
        // guarded server-side, and a hard failure here would block an edit the
        // user can otherwise perform.
      });

    return () => { cancelled = true; };
  }, [open, agent?.id, agent?.instructions]);

  // Defensive body-scroll restore. antd v6 manages a refcount-based scroll
  // locker on <body>; under React 19 strict mode + heavy children (the
  // markdown editor here is large) the lock count occasionally drifts and
  // leaves <body> with overflow:hidden after the drawer closes — the user
  // sees the page apparently "stuck" with no way to scroll up. Clear our
  // own contribution explicitly on close and on unmount.
  useEffect(() => {
    if (open) return;
    // Run on next frame so antd's own cleanup runs first.
    const id = window.requestAnimationFrame(() => {
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
      }
      if (document.body.style.paddingRight) {
        document.body.style.paddingRight = '';
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [open]);
  useEffect(() => {
    return () => {
      // On unmount, force-restore body scroll. Belt-and-braces — covers the
      // case where the parent unmounts the drawer while still open.
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, []);

  // Capability segments — surfaced in the effective-prompt preview and the
  // sidebar list. Pulled off the overview when the parent has it loaded.
  const capabilitySegments = useMemo(() => {
    const segs = overview?.systemPrompts ?? [];
    return segs.map((s) => ({
      id: s.id,
      title: s.title ?? s.name ?? `Segment ${s.id}`,
      body: s.baseInstructions ?? '',
    }));
  }, [overview]);

  const segmentChars = capabilitySegments.reduce((acc, s) => acc + (s.body?.length ?? 0), 0);
  const effectiveBody = useMemo(() => {
    if (capabilitySegments.length === 0) return body;
    const parts = [body];
    for (const s of capabilitySegments) {
      parts.push(`\n\n---\n\n## ${s.title}\n\n${s.body}`);
    }
    return parts.join('');
  }, [body, capabilitySegments]);

  const totalTokens = tokensFor(body) + tokensFor(capabilitySegments.map((s) => s.body).join(''));
  const dirty = body !== (agent?.instructions ?? '');
  useHoldUnsavedWork(open && dirty, `instructions for ${agent?.displayName ?? 'this agent'}`);

  const lints = useMemo(() => computeLints(body, capabilitySegments.map((s) => s.title)), [body, capabilitySegments]);

  // ── AI draft ─────────────────────────────────────────────────────────
  const runDraft = async (mode: InstructionDraftMode) => {
    if (!agent) return;
    if (mode === 'addSection' && draftPrompt.trim().length < 5) {
      void message.warning('Describe the section you want.');
      return;
    }
    if (mode === 'rewriteSelection' && selectionText.trim().length < 5) {
      void message.warning('Highlight a passage in the editor first.');
      return;
    }
    setDrafting(true);
    setDraftError(null);
    try {
      const res = await draftInstruction({
        agentId: agent.id,
        mode,
        userRequest: draftPrompt || undefined,
        currentInstructions: body,
        selection: mode === 'rewriteSelection' ? selectionText : undefined,
      });
      if (mode === 'findRedundancy') {
        setDraftSnapshot({
          mode,
          proposedBody: null,
          rationale: res.rationale ?? '',
          redundancy: res.redundancyHits ?? [],
        });
      } else if (res.proposedInstructions) {
        // Stash the previous body so the user can revert if they don't
        // like the AI's edit. The "Apply" / "Discard" buttons toggle
        // between the two via setBody / restore.
        setPreviousBody(body);
        setBody(res.proposedInstructions);
        setDraftSnapshot({
          mode,
          proposedBody: res.proposedInstructions,
          rationale: res.rationale ?? '',
          redundancy: [],
        });
      } else {
        setDraftSnapshot({
          mode,
          proposedBody: null,
          rationale: res.rationale ?? '(model returned no proposal)',
          redundancy: [],
        });
      }
    } catch (err) {
      setDraftError((err as Error).message);
    } finally {
      setDrafting(false);
    }
  };

  const revertDraft = () => {
    if (previousBody == null) return;
    setBody(previousBody);
    setPreviousBody(null);
    setDraftSnapshot(null);
  };

  // ── Save ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!agent) return;
    setSaving(true);
    try {
      const next = await updateAgent({ ...agent, instructions: body });
      void message.success('Instruction saved.');
      if (onSaved) await onSaved(next);
      onClose();
    } catch (err) {
      void message.error('Save failed: ' + (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // ── Export ───────────────────────────────────────────────────────────
  const handleExport = () => {
    if (!agent) return;
    const slug = (agent.displayName ?? `agent-${agent.id}`)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    const filename = `${slug || `agent-${agent.id}`}-instructions.md`;
    const blob = new Blob([body], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Capture textarea selection for "Rewrite selection" mode ──────────
  // MDEditor renders its own <textarea> inside the wrapper. We can't pass a
  // ref through textareaProps (it's not in ITextAreaProps), so we walk the
  // DOM from the wrapper on every selection event. Cheap (one querySelector).
  const onSelect = () => {
    const el = editorWrapRef.current?.querySelector('textarea');
    if (!el) return;
    const { selectionStart, selectionEnd, value } = el;
    if (selectionStart === selectionEnd) {
      setSelectionText('');
      return;
    }
    setSelectionText(value.substring(selectionStart, selectionEnd));
  };

  // Token meter color tier — green (low), amber (mid), red (over budget).
  const tokenTier =
    totalTokens > TOKEN_BUDGET_LIMIT * 0.8
      ? 'red'
      : totalTokens > TOKEN_BUDGET_LIMIT * 0.5
      ? 'amber'
      : 'green';
  const tokenColor =
    tokenTier === 'red' ? colors.red[600]
      : tokenTier === 'amber' ? colors.amber[600]
      : colors.green[600];

  return (
    <Drawer
      title={
        <Space size={8}>
          <ThunderboltOutlined style={{ color: colors.violet[500] }} />
          <span>Edit primary instruction</span>
          {agent && (
            <Tag color="violet" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {agent.displayName ?? `agent-${agent.id}`}
            </Tag>
          )}
          {dirty && <Tag color="orange">Unsaved</Tag>}
        </Space>
      }
      width="92%"
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Tooltip title="See and restore previous saved versions of this instruction">
            <Button
              icon={<HistoryOutlined />}
              onClick={() => setHistoryOpen(true)}
              disabled={!agent}
            >
              History
            </Button>
          </Tooltip>
          <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!agent}>
            Export .md
          </Button>
          <Button icon={<CloseOutlined />} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
            disabled={!dirty}
          >
            Save instruction
          </Button>
        </Space>
      }
    >
      {!agent ? (
        <div style={{ padding: 60, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr) 320px',
            gap: 16,
            height: '100%',
          }}
          data-color-mode={isDark ? 'dark' : 'light'}
        >
          {/* ── Pane 1: Editor ─────────────────────────────────────────── */}
          <div style={{ overflowY: 'auto' }} ref={editorWrapRef} onMouseUp={onSelect} onKeyUp={onSelect}>
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {body.length.toLocaleString()} chars · ≈ {tokensFor(body).toLocaleString()} tokens · {body.split('\n').length.toLocaleString()} lines
              </Text>
              {selectionText && (
                <Tag color="violet" style={{ marginLeft: 'auto' }}>
                  Selection: {selectionText.length} chars
                </Tag>
              )}
            </div>
            <MDEditor
              value={body}
              onChange={(v) => setBody(v ?? '')}
              height={680}
              preview="edit"
              textareaProps={{
                placeholder: 'Markdown body of the agent instruction…',
              }}
            />
          </div>

          {/* ── Pane 2: Preview (primary or effective) ───────────────── */}
          <div
            style={{
              overflowY: 'auto',
              background: isDark ? colors.slate[900] : colors.slate[50],
              border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
              borderRadius: 8,
              padding: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text strong style={{ fontSize: 12 }}>
                PREVIEW
              </Text>
              <Select<'primary' | 'effective'>
                size="small"
                value={previewMode}
                onChange={setPreviewMode}
                style={{ width: 180 }}
                options={[
                  { value: 'primary', label: 'Primary instruction' },
                  {
                    value: 'effective',
                    label: `Effective prompt (+${capabilitySegments.length} segs)`,
                    disabled: capabilitySegments.length === 0,
                  },
                ]}
              />
            </div>
            <div data-color-mode={isDark ? 'dark' : 'light'}>
              <MDEditor.Markdown
                source={previewMode === 'effective' ? effectiveBody : body}
                style={{ background: 'transparent', fontSize: 13 }}
              />
            </div>
          </div>

          {/* ── Pane 3: Smart sidebar ────────────────────────────────── */}
          <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Token budget meter */}
            <SidebarCard title="Token budget" isDark={isDark}>
              <div
                style={{
                  height: 6,
                  background: isDark ? colors.slate[700] : colors.slate[200],
                  borderRadius: 3,
                  overflow: 'hidden',
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min(100, (totalTokens / TOKEN_BUDGET_LIMIT) * 100)}%`,
                    background: tokenColor,
                    transition: 'width 200ms ease',
                  }}
                />
              </div>
              <Text style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace", color: tokenColor }}>
                {totalTokens.toLocaleString()} / {TOKEN_BUDGET_LIMIT.toLocaleString()} tokens
              </Text>
              <div style={{ fontSize: 11, color: isDark ? colors.slate[400] : colors.slate[500], marginTop: 4 }}>
                Primary {tokensFor(body).toLocaleString()} + capabilities {tokensFor(
                  capabilitySegments.map((s) => s.body).join(''),
                ).toLocaleString()} = effective prompt
                {segmentChars > 0 && ` · ${segmentChars.toLocaleString()} chars from ${capabilitySegments.length} segments`}
              </div>
            </SidebarCard>

            {/* AI co-pilot */}
            <SidebarCard
              title={
                <Space>
                  <ThunderboltOutlined /> <span>AI co-pilot</span>
                </Space>
              }
              isDark={isDark}
            >
              <Tabs
                size="small"
                items={[
                  {
                    key: 'tighten',
                    label: <Tooltip title="Reduce tokens without losing rules"><CompressOutlined /></Tooltip>,
                    children: (
                      <CopilotPane
                        description="Shrink the instruction by ≥15% while preserving every clinical rule and named entity."
                        action={
                          <Button
                            block
                            type="primary"
                            icon={<CompressOutlined />}
                            loading={drafting}
                            onClick={() => runDraft('tighten')}
                          >
                            Tighten
                          </Button>
                        }
                      />
                    ),
                  },
                  {
                    key: 'addSection',
                    label: <Tooltip title="Insert a new section"><FileAddOutlined /></Tooltip>,
                    children: (
                      <CopilotPane
                        description="Describe a new section to add. The model places it in a logical location and leaves the rest intact."
                        action={
                          <>
                            <TextArea
                              rows={3}
                              placeholder='e.g. "add a SAFETY section that says no specific medication names"'
                              value={draftPrompt}
                              onChange={(e) => setDraftPrompt(e.target.value)}
                              style={{ fontSize: 13, marginBottom: 8 }}
                            />
                            <Button
                              block
                              type="primary"
                              icon={<FileAddOutlined />}
                              loading={drafting}
                              onClick={() => runDraft('addSection')}
                              disabled={draftPrompt.trim().length < 5}
                            >
                              Draft new section
                            </Button>
                          </>
                        }
                      />
                    ),
                  },
                  {
                    key: 'rewriteSelection',
                    label: <Tooltip title="Rewrite the highlighted passage"><HighlightOutlined /></Tooltip>,
                    children: (
                      <CopilotPane
                        description={
                          selectionText
                            ? `${selectionText.length} chars selected — describe how to rewrite it.`
                            : 'Highlight text in the editor first, then describe the rewrite.'
                        }
                        action={
                          <>
                            <TextArea
                              rows={2}
                              placeholder='e.g. "make it more concise" or "use bullet points"'
                              value={draftPrompt}
                              onChange={(e) => setDraftPrompt(e.target.value)}
                              style={{ fontSize: 13, marginBottom: 8 }}
                              disabled={!selectionText}
                            />
                            <Button
                              block
                              type="primary"
                              icon={<HighlightOutlined />}
                              loading={drafting}
                              onClick={() => runDraft('rewriteSelection')}
                              disabled={!selectionText}
                            >
                              Rewrite selection
                            </Button>
                          </>
                        }
                      />
                    ),
                  },
                  {
                    key: 'findRedundancy',
                    label: <Tooltip title="Find rules already covered by capability segments"><SearchOutlined /></Tooltip>,
                    children: (
                      <CopilotPane
                        description={
                          capabilitySegments.length === 0
                            ? 'Agent has no capability segments — nothing to compare against.'
                            : `Compares your instruction with ${capabilitySegments.length} capability segment${capabilitySegments.length === 1 ? '' : 's'} and flags overlapping rules.`
                        }
                        action={
                          <Button
                            block
                            type="primary"
                            icon={<SearchOutlined />}
                            loading={drafting}
                            onClick={() => runDraft('findRedundancy')}
                            disabled={capabilitySegments.length === 0}
                          >
                            Find redundancy
                          </Button>
                        }
                      />
                    ),
                  },
                ]}
              />
              {draftError && (
                <Alert type="error" showIcon message={draftError} style={{ marginTop: 8 }} closable onClose={() => setDraftError(null)} />
              )}
              {draftSnapshot && (
                <DraftResult
                  snapshot={draftSnapshot}
                  hasPrevious={previousBody != null}
                  onRevert={revertDraft}
                  isDark={isDark}
                />
              )}
            </SidebarCard>

            {/* Linked capability segments */}
            <SidebarCard
              title={
                <Space>
                  <SwapOutlined /> <span>Linked segments</span>
                  <Tag>{capabilitySegments.length}</Tag>
                </Space>
              }
              isDark={isDark}
            >
              {capabilitySegments.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  No capability segments link to this agent.
                </Text>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {capabilitySegments.map((s) => (
                    <div
                      key={s.id}
                      style={{
                        padding: '6px 8px',
                        background: isDark ? colors.slate[800] : '#FFFFFF',
                        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                        borderRadius: 6,
                        fontSize: 12,
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{s.title}</div>
                      <Text type="secondary" style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
                        {s.body.length.toLocaleString()} chars
                      </Text>
                    </div>
                  ))}
                </div>
              )}
            </SidebarCard>

            {/* Lint */}
            <SidebarCard
              title={
                <Space>
                  <span>Lint</span>
                  <Tag color={lints.length === 0 ? 'green' : 'orange'}>{lints.length}</Tag>
                </Space>
              }
              isDark={isDark}
            >
              {lints.length === 0 ? (
                <Space size={6}>
                  <CheckCircleOutlined style={{ color: colors.green[500] }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    No issues — looks healthy.
                  </Text>
                </Space>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {lints.map((l, i) => (
                    <Tooltip key={i} title={l.detail}>
                      <div
                        style={{
                          fontSize: 12,
                          color: isDark ? colors.amber[500] : colors.amber[700],
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 6,
                        }}
                      >
                        <WarningOutlined style={{ marginTop: 2 }} />
                        <span>{l.message}</span>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              )}
            </SidebarCard>
          </div>
        </div>
      )}

      {/* Version history — sibling drawer that overlays at 60% width on top
          of this editor. Restoring an older version updates this drawer's
          working buffer immediately so the user sees the swap. */}
      <InstructionHistoryDrawer
        open={historyOpen}
        agent={agent}
        currentInstructions={agent?.instructions ?? ''}
        onClose={() => setHistoryOpen(false)}
        onRestored={(next) => {
          // Sync the editor body to the freshly restored content. Server
          // already saved + recorded a new history row capturing what was
          // previously live, so closing the history drawer is fine.
          setBody(next.instructions ?? '');
          setHistoryOpen(false);
          // Bubble the updated agent back to the parent so the detail page
          // can refresh char counts / preview.
          if (agent) {
            void onSaved?.({ ...agent, instructions: next.instructions });
          }
        }}
      />
    </Drawer>
  );
});

// ── Sub-components ─────────────────────────────────────────────────────────

function SidebarCard({
  title,
  children,
  isDark,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  isDark: boolean;
}) {
  return (
    <div
      style={{
        background: isDark ? colors.slate[900] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 8,
        padding: 12,
      }}
    >
      <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 600 }}>{title}</div>
      {children}
    </div>
  );
}

function CopilotPane({
  description,
  action,
}: {
  description: string;
  action: React.ReactNode;
}) {
  return (
    <div style={{ paddingTop: 4 }}>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
        {description}
      </Text>
      {action}
    </div>
  );
}

function DraftResult({
  snapshot,
  hasPrevious,
  onRevert,
  isDark,
}: {
  snapshot: DraftSnapshot;
  hasPrevious: boolean;
  onRevert: () => void;
  isDark: boolean;
}) {
  return (
    <div
      style={{
        marginTop: 10,
        padding: '8px 10px',
        background: isDark ? colors.slate[800] : colors.slate[50],
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 6,
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <Text strong style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {snapshot.mode === 'findRedundancy' ? 'Redundancy report' : 'AI rationale'}
        </Text>
        {hasPrevious && (
          <Button size="small" type="link" onClick={onRevert}>
            Revert
          </Button>
        )}
      </div>
      {snapshot.rationale && (
        <div style={{ color: isDark ? colors.slate[300] : colors.slate[700], marginBottom: 6 }}>
          {snapshot.rationale}
        </div>
      )}
      {snapshot.mode === 'findRedundancy' && snapshot.redundancy.length === 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No overlap detected — your primary instruction doesn't duplicate anything in the linked segments.
        </Text>
      )}
      {snapshot.mode === 'findRedundancy' && snapshot.redundancy.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {snapshot.redundancy.map((h, i) => (
            <div
              key={i}
              style={{
                padding: '6px 8px',
                background: isDark ? colors.slate[900] : '#FFFFFF',
                border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                borderRadius: 4,
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 11.5 }}>
                <Tag color="orange">{h.capabilityName ?? 'segment'}</Tag>
                {h.recommendation}
              </div>
              {h.overlappingSnippet && (
                <pre
                  style={{
                    margin: '4px 0 0',
                    fontSize: 11,
                    fontFamily: "'JetBrains Mono', monospace",
                    whiteSpace: 'pre-wrap',
                    color: isDark ? colors.slate[400] : colors.slate[600],
                  }}
                >
                  {h.overlappingSnippet}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Lint rules ─────────────────────────────────────────────────────────────

interface Lint {
  message: string;
  detail: string;
}

function computeLints(body: string, segmentTitles: string[]): Lint[] {
  const out: Lint[] = [];
  if (!body || body.trim().length < 50) {
    out.push({ message: 'Instruction is very short', detail: 'A useful agent instruction is usually several paragraphs at minimum.' });
  }
  // Heuristic: ALL-CAPS section headings. Most agent instructions in this
  // codebase use IDENTITY / CAPABILITIES / CONSTRAINTS / EXAMPLES.
  const hasIdentity = /\bIDENTITY\b/.test(body);
  if (!hasIdentity) {
    out.push({
      message: 'Missing IDENTITY section',
      detail: 'Most agents in this codebase open with an IDENTITY block stating who the agent is and who it talks to.',
    });
  }
  // Detect capability-segment titles repeated in the primary body — likely
  // duplication between primary and segment text.
  for (const t of segmentTitles) {
    if (!t) continue;
    if (body.toLowerCase().includes(t.toLowerCase()) && body.length > 200) {
      out.push({
        message: `Body mentions "${t}" by name`,
        detail: 'The capability segment is concatenated automatically. Mentioning it by title in the primary instruction is usually redundant — use Find redundancy to confirm.',
      });
      break;
    }
  }
  // Tool-name leakage — see memory feedback_agent_instructions.md.
  const toolPatternHits = body.match(/\b[A-Z][a-zA-Z0-9_]*(?:Plugin|Tool|Function|_)\w*/g);
  if (toolPatternHits && toolPatternHits.length > 0) {
    const sample = toolPatternHits.slice(0, 3).join(', ');
    out.push({
      message: 'Possible tool/function names referenced',
      detail: `Per project convention, instructions describe behavior — not C# identifiers. Found: ${sample}.`,
    });
  }
  // Markdown sanity — unmatched fences will break preview rendering.
  const fences = (body.match(/```/g) ?? []).length;
  if (fences % 2 !== 0) {
    out.push({
      message: 'Unmatched ``` code fence',
      detail: 'An odd number of triple-backtick fences will break the rendered preview and the runtime prompt.',
    });
  }
  return out;
}
